/* eslint-disable max-lines */
import {
  BizNestClientError, FrontendLogger, Iauthresponse,
  IauthTokenResponse,
  IchangeEmailRes, IchangePhoneRes,
  IpermProp,
  IrecoveryTokenRes, IresetPasswordRes, IsignupRes, Iuser,
  IuserPermission,
  IwsAuthTokenResponse
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';
import { User } from '../defines/user.define';

/**
 * Login payload for username/password authentication.
 *
 * @public
 */
export interface IidentityInfo {

  /** Optional number of days to remember the session. */
  rememberMeDays?: number;

  /** Login email, if using email credential flow. */
  email?: string;

  /** Login phone, if using phone credential flow. */
  phone?: string;
}

export interface IidentityRes {
  method: Iuser['preferredLoginMethod'];
  identifier: string;
}

/**
 * Login payload for username/password authentication.
 *
 * @public
 */
export interface IloginInfo {

  /** Optional number of days to remember the session. */
  rememberMeDays?: number;

  /** Login email, if using email credential flow. */
  email?: string;

  /** Login phone, if using phone credential flow. */
  phone?: string;

  /** Plain-text password to authenticate. */
  password?: string;

  /** Login method, if using passwordless flow. */
  loginMethod: Iuser['preferredLoginMethod'];
}

/**
 * Signup payload for new account creation.
 *
 * @public
 */
export interface IsignupInfo {

  /** Email to register (optional if using phone). */
  email?: string;

  /** Phone to register (optional if using email). */
  phone?: string;

  /** Account password. */
  password: string;

  /** First name of the user. */
  firstName?: string;

  /** Last name of the user. */
  lastName?: string;
}

/**
 * Signup payload for new account creation.
 *
 * @public
 */
export interface IpasswordlessSignupInfo {

  /** Email to register (optional if using phone). */
  email?: string;

  /** Phone to register (optional if using email). */
  phone?: string;

  /** First name of the user. */
  firstName?: string;

  /** Last name of the user. */
  lastName?: string;
}


/**
 * Recovery payload for initiating account recovery.
 *
 * @public
 */
export interface IsendRecoveryTokenInfo {

  /** Email to recover (optional if using phone). */
  email?: string;

  /** Phone to recover (optional if using email). */
  phone?: string;

  /** Alternate recovery phone, if used by the flow. */
  recoveryPhone?: string;

  /** Alternate recovery email, if used by the flow. */
  recoveryEmail?: string;
}

/**
 * Payload for changing email address.
 *
 * @public
 */
export interface IchangeEmailInfo {
  email: string;
}

/**
 * Recovery payload for initiating account recovery.
 *
 * @public
 */
export interface IchangePhoneInfo {
  phone: string;
}

/**
 * Payload for initiating password reset.
 *
 * @public
 */
export interface IpasswordResetPayload {

  /** Email to recover (optional if using phone). */
  email?: string;

  /** Phone to recover (optional if using email). */
  phone?: string;

  /** Alternate recovery phone, if used by the flow. */
  recoveryPhone?: string;

  /** Alternate recovery email, if used by the flow. */
  recoveryEmail?: string;
}

/**
 * Payload for account verification.
 *
 * @public
 */
export interface IverificationInfo {

  /** Verification code provided by email/SMS/link. */
  verificationCode: string;
}

/**
 * Payload for resetting password.
 *
 * @public
 */
export interface IresetPasswordInfo {

  /** Verification code provided by email/SMS/link. */
  verificationCode: string;

  /** New password to set. */
  password: string;
}

/**
 * Payload for changing password.
 *
 * @public
 */
export interface IchangePasswordInfo {

  /** Current password (required for password change), if social login or passwordless it might have not be set. */
  oldPassword?: string;

  /** New password to set. */
  password: string;
}

/**
 * Payload for adding password to passwordless account.
 *
 * @public
 */
export interface IaddPasswordPayload {

  /** Verification code provided by email/SMS/link. */
  verificationCode: string;

  /** Password to set for the account. */
  password: string;
}


/**
 * Retrieves the currently authenticated user (JWT-based).
 *
 * @returns A promise that resolves to the authenticated {@link User}.
 */
export const me = async(): Promise<User> => {
  FrontendLogger.debug('AuthService:me::');
  const observer$ = AuthClient.ehttp
    .makeGet<Iuser>('/auth/me');

  const res = await lastValueFrom(observer$);

  return new User(res);
};

/**
 * Retrieves the currently authenticated admin metadata.
 *
 * @returns A promise resolving to the auth response plus default currency.
 */
export const meAdmin = (): Promise<Iauthresponse & { defaultCurrency: string }> => {
  FrontendLogger.debug('AuthService:meAdmin::');
  const observer$ = AuthClient.ehttp
    .makeGet<Iauthresponse & { defaultCurrency: string }>('/admin/me');

  return lastValueFrom(observer$);
};

/**
 * Initiates an admin login (OTP or token-based depending on backend).
 *
 * @param params - Optional email or phone to identify the admin.
 * @returns A promise resolving to an auth token response.
 */
export const adminLogin = ({
  email,
  phone
}: {
  email?: string;
  phone?: string;
}): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:adminLogin');
  const observer$ = AuthClient.ehttp
    .makePost<IauthTokenResponse>('/admin/login', { email, phone });

  return lastValueFrom(observer$);
};

/**
 * Verifies an admin login challenge (e.g., OTP), finalizing authentication.
 *
 * @param params - Contains the verification token and optional rememberMeDays.
 * @returns A promise resolving to the auth response.
 */
export const adminVerify = ({
  verificationToken,
  rememberMeDays
}: {
  verificationToken: string;
  rememberMeDays?: number;
}): Promise<Iauthresponse> => {
  FrontendLogger.debug('AuthService:adminVerify');
  const observer$ = AuthClient.ehttp
    .makePost<Iauthresponse>('/admin/verify', { verificationToken, rememberMeDays });

  return lastValueFrom(observer$);
};


/**
 * Begins a Google OAuth login by navigating to the backend-provided URL.
 *
 * @param queryParams - Optional parameters to pass to the login URL.
 * @returns The opened window handle (if available).
 * @throws {@link BizNestClientError} If the Google login URL is not configured.
 */
export const googleLogin = (queryParams?: Record<string, string>): Window | null => {
  FrontendLogger.debug('AuthService:googleLogin');

  if (!AuthClient.socialCallUrls?.google) {
    throw new BizNestClientError('Google login URL not found');
  }

  let targetUrl = AuthClient.socialCallUrls.google;

  // Append query parameters if provided
  if (queryParams) {
    const url = new URL(targetUrl, window.location.origin);

    Object.keys(queryParams).forEach(key => {
      if (queryParams[key]) {
        url.searchParams.set(key, queryParams[key]);
      }
    });

    targetUrl = url.toString();
  }


  return window?.open(targetUrl, '_self');
};


/**
 * Begins a Facebook OAuth login by navigating to the backend-provided URL.
 *
 * @param queryParams - Optional parameters to pass to the login URL.
 * @returns The opened window handle (if available).
 * @throws {@link BizNestClientError} If the Facebook login URL is not configured.
 */
export const facebookLogin = (queryParams?: Record<string, string>): Window | null => {
  FrontendLogger.debug('AuthService:facebookLogin');

  if (!AuthClient.socialCallUrls?.facebook) {
    throw new BizNestClientError('Facebook login URL not found');
  }

  let targetUrl = AuthClient.socialCallUrls.facebook;

  // Append query parameters if provided
  if (queryParams) {
    const url = new URL(targetUrl, window.location.origin);

    Object.keys(queryParams).forEach(key => {
      if (queryParams[key]) {
        url.searchParams.set(key, queryParams[key]);
      }
    });

    targetUrl = url.toString();
  }

  return window?.open(targetUrl, '_self');
};

/**
 * Authenticates a user with email/phone and password.
 *
 * @param payload - The login payload; must include either email or phone.
 * @returns A promise resolving to an auth token response.
 * @throws {@link BizNestClientError} If neither email nor phone is provided.
 */
export const identity = (payload: IidentityInfo): Promise<IidentityRes> => {
  if (!payload.email && !payload.phone) {
    throw new BizNestClientError('Either email or phone number must be provided');
  }
  FrontendLogger.debug('AuthService:identity');
  const observer$ = AuthClient.ehttp.makePost<IidentityRes>(
    '/auth/identity',
    { email: payload.email, phone: payload.phone }
  );

  return lastValueFrom(observer$);
};

/**
 * Authenticates a user with email/phone and password.
 *
 * @param payload - The login payload; must include either email or phone.
 * @returns A promise resolving to an auth token response.
 * @throws {@link BizNestClientError} If neither email nor phone is provided.
 */
export const login = (payload: IloginInfo): Promise<IauthTokenResponse & {
  identifier: string; twoFaMethodEnabled: boolean; twoFaMethod: string; }> => {
  if (!payload.email && !payload.phone) {
    throw new BizNestClientError('Either email or phone number must be provided');
  }
  FrontendLogger.debug('AuthService:login');
  const observer$ = AuthClient.ehttp.makePost<IauthTokenResponse & {
    identifier: string; twoFaMethodEnabled: boolean; twoFaMethod: string; }>(
      '/auth/login',
      { email: payload.email,
        phone: payload.phone,
        password: payload.password,
        rememberMeDays: payload.rememberMeDays,
        loginMethod: payload.loginMethod
      }
    );

  return lastValueFrom(observer$);
};

/**
 * Verifies a user's 2FA code.
 *
 * @param payload - The 2FA verification payload; must include the verification code.
 * @returns A promise resolving to an auth token response.
 * @throws {@link BizNestClientError} If the verification code is missing.
 */
export const login2fa = (payload: { verificationCode: string }): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:login2fa');
  const observer$ = AuthClient.ehttp.makePost<IauthTokenResponse>(
    '/auth/2fa/login',
    payload
  );

  return lastValueFrom(observer$);
};

/**
 * Registers a new user.
 *
 * @param payload - The signup payload; must include either email or phone.
 * @returns A promise resolving to an auth response.
 * @throws {@link BizNestClientError} If neither email nor phone is provided.
 */
export const signup = (payload: IsignupInfo): Promise<IsignupRes> => {
  if (!payload.email && !payload.phone) {
    throw new BizNestClientError('Either email or phone number must be provided');
  }
  const details = {
    email: payload.email,
    phone: payload.phone,
    password: payload.password,
    firstName: payload.firstName,
    lastName: payload.lastName
  };

  const observer$ = AuthClient.ehttp
    .makePost<IsignupRes>('/auth/signup', details);

  return lastValueFrom(observer$);
};

/**
 * Initiates password reset.
 *
 * @param payload - The recovery payload; must include either email or phone.
 * @returns A promise resolving to a recovery token response.
 * @throws {@link BizNestClientError} If neither email nor phone is provided.
 */
export const initiatePasswordReset = (payload: IpasswordResetPayload): Promise<IresetPasswordRes> => {
  if (!payload.email && !payload.phone) {
    throw new BizNestClientError('Either email or phone number must be provided');
  }
  FrontendLogger.debug('AuthService:sendRecoveryToken');
  const observer$ = AuthClient.ehttp
    .makePost<IrecoveryTokenRes>('/auth/password-resets/initiate', payload);

  return lastValueFrom(observer$);
};

/**
 * Resets user password using a code or link.
 *
 * @param payload - The reset password payload.
 * @returns A promise resolving to an auth token response.
 */
export const finalizePasswordReset = (payload: IresetPasswordInfo): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<IauthTokenResponse>('/auth/password-resets/finalize', payload);

  return lastValueFrom(observer$);
};

/**
 * Finalizes password reset.
 *
 * @param payload - The reset password payload.
 * @returns A promise resolving to an auth token response.
 */
export const changePassword = async(payload: IchangePasswordInfo): Promise<User> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/change-password', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Changes user login method.
 *
 * @param payload - The change login method payload.
 * @returns A promise resolving to a user object.
 */
export const changeLoginMethod = async(payload: { method: 'password' | 'passwordless' }): Promise<User> => {
  FrontendLogger.debug('AuthService:changeLoginMethod');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/change-login-method', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Initiates change email.
 *
 * @param payload - The change email payload.
 * @returns A promise resolving to a recovery token response.
 */
export const initiateChangeEmail = (payload: IchangeEmailInfo): Promise<IchangeEmailRes> => {
  FrontendLogger.debug('AuthService:sendRecoveryToken');
  const observer$ = AuthClient.ehttp
    .makePost<IchangeEmailRes>('/auth/change-email/initiate', payload);

  return lastValueFrom(observer$);
};

/**
 * Finalizes change email.
 *
 * @param payload - The change email payload.
 * @returns A promise resolving to a change email response.
 */
export const finalizeChangeEmail = async(payload: { verificationCode: string }): Promise<User> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/change-email/finalize', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Initiates change phone.
 *
 * @param payload - The change phone payload.
 * @returns A promise resolving to a recovery token response.
 */
export const initiateChangePhone = (payload: IchangePhoneInfo): Promise<IchangePhoneRes> => {
  FrontendLogger.debug('AuthService:sendRecoveryToken');
  const observer$ = AuthClient.ehttp
    .makePost<IchangePhoneRes>('/auth/change-phone/initiate', payload);

  return lastValueFrom(observer$);
};

/**
 * Finalizes change phone.
 *
 * @param payload - The change phone payload.
 * @returns A promise resolving to a change phone response.
 */
export const finalizeChangePhone = async(payload: { verificationCode: string }): Promise<User> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/change-phone/finalize', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Initiates 2FA enrollment.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to a recovery token response.
 */
export const initiate2faEnrollment = (payload: {
  method: 'email' | 'phone';
  email?: string;
  phone?: string;
}): Promise<{ message: string }> => {
  FrontendLogger.debug('AuthService:sendRecoveryToken');
  const observer$ = AuthClient.ehttp
    .makePost<{ message: string }>('/auth/2fa/enroll/initiate', payload);

  return lastValueFrom(observer$);
};

/**
 * Finalizes 2FA enrollment.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to a change phone response.
 */
export const finalize2faEnrollment = async(payload: {
  verificationCode: string;
}): Promise<User> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/2fa/enroll/finalize', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Initiates 2FA change.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to a recovery token response.
 */
export const initiate2faChange = (payload: {
  method: 'email' | 'phone';
  email?: string;
  phone?: string;
}): Promise<{ message: string }> => {
  FrontendLogger.debug('AuthService:sendRecoveryToken');
  const observer$ = AuthClient.ehttp
    .makePost<{ message: string }>('/auth/2fa/change/initiate', payload);

  return lastValueFrom(observer$);
};

/**
 * Finalizes 2FA change.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to a change phone response.
 */
export const finalize2faChange = async(payload: {
  verificationCode: string;
}): Promise<User> => {
  FrontendLogger.debug('AuthService:finalizePasswordReset');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/2fa/change/finalize', payload);

  return new User(await lastValueFrom(observer$));
};

/**
 * Initiates 2FA disable.
 *
 * @returns A promise resolving to a recovery token response.
 */
export const initiate2faDisable = (): Promise<{ message: string }> => {
  FrontendLogger.debug('AuthService:initiate2faDisable');
  const observer$ = AuthClient.ehttp
    .makePost<{ message: string }>('/auth/2fa/disable/initiate', {});

  return lastValueFrom(observer$);
};

/**
 * Finalizes 2FA disable.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to a change phone response.
 */
export const finalize2faDisable = async(payload: {
  verificationCode: string;
}): Promise<User> => {
  FrontendLogger.debug('AuthService:finalize2faDisable');
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/2fa/disable/finalize', payload);

  return new User(await lastValueFrom(observer$));
};


/**
 * Verifies user account using a code or link.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to an auth token response.
 */
export const verifyAccount = (payload: IverificationInfo): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:verify');
  const observer$ = AuthClient.ehttp
    .makePost<IauthTokenResponse>('/auth/verify-account', payload);

  return lastValueFrom(observer$);
};

/**
 * Resends verification code to user's email or phone.
 *
 * @param payload - The resend verification code payload.
 * @returns A promise resolving to a resend verification code response.
 */
export const resendVerificationCode = (payload: { email?: string; phone?: string }): Promise<{ message: string }> => {
  FrontendLogger.debug('AuthService:resendVerificationCode');
  const observer$ = AuthClient.ehttp
    .makePost<{ message: string }>('/auth/resend-verification-code', payload);

  return lastValueFrom(observer$);
};

/**
 * Resends verification code to user's email or phone.
 *
 * @param payload - The resend verification code payload.
 * @returns A promise resolving to a resend verification code response.
 */
export const resendVerificationCodePasswordless = (payload: { email?: string;
  phone?: string; }): Promise<{ message: string }> => {
  FrontendLogger.debug('AuthService:resendVerificationCodePasswordless');
  const observer$ = AuthClient.ehttp
    .makePost<{ message: string }>('/auth/resend-verification-code/passwordless', payload);

  return lastValueFrom(observer$);
};


/**
 * Registers a new user passwordless (alternate signup flow).
 *
 * @param payload - The signup payload; must include either email or phone.
 * @returns A promise resolving to an auth response.
 * @throws {@link BizNestClientError} If neither email nor phone is provided.
 */
export const signupPasswordless = (payload: IpasswordlessSignupInfo): Promise<IsignupRes> => {
  FrontendLogger.debug('AuthService:signupPasswordless');
  if (!payload.email && !payload.phone) {
    throw new BizNestClientError('Either email or phone number must be provided');
  }

  const observer$ = AuthClient.ehttp
    .makePost<IsignupRes>('/auth/passwordless/signup', payload);

  return lastValueFrom(observer$);
};

/**
 * Verifies passwordless verification using a code or link.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to an auth token response.
 */
export const verifyPasswordless = (payload: IverificationInfo): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:verifyPasswordless');
  const observer$ = AuthClient.ehttp
    .makePost<IauthTokenResponse>('/auth/passwordless/verify', payload);

  return lastValueFrom(observer$);
};

/**
 * Verifies passwordless verification using a code or link.
 *
 * @param payload - The verification payload.
 * @returns A promise resolving to an auth token response.
 */
export const loginPasswordless = (payload: IverificationInfo): Promise<IauthTokenResponse> => {
  FrontendLogger.debug('AuthService:loginPasswordless');
  const observer$ = AuthClient.ehttp
    .makePost<IauthTokenResponse>('/auth/passwordless/login', payload);

  return lastValueFrom(observer$);
};

/**
 * Retrieves available recovery methods (phones/emails) for a user.
 *
 * @param userId - The user identifier.
 * @returns A promise resolving to the recovery phone numbers and emails.
 */
export const recoveryMethod = (userId: string): Promise<{
  recoveryPhoneNumbers: string[]; recoveryEmails: string[]; }> => {
  const observer$ = AuthClient.ehttp
    .makeGet<{recoveryPhoneNumbers: string[]; recoveryEmails: string[] }>(`/auth/recovery-methods-list/${userId}`);

  return lastValueFrom(observer$);
};

/**
 * Fetches permissions assigned to a user.
 *
 * @param userId - The user identifier.
 * @returns A promise resolving to the user's permissions.
 */
export const getUserPermissions = (userId: string): Promise<IuserPermission> => {
  const observer$ = AuthClient.ehttp
    .makeGet<IuserPermission>(`/auth/get-user-permissions/${userId}`);

  return lastValueFrom(observer$);
};


/**
 * Fetches permissions assigned to the current user.
 *
 * @returns A promise resolving to the user's permissions.
 */
export const getMyPermissions = (): Promise<IuserPermission> => {
  const observer$ = AuthClient.ehttp
    .makeGet<IuserPermission>('/auth/get-my-permissions');

  return lastValueFrom(observer$);
};

/**
 * Updates a user's permissions.
 *
 * @param userId - The user identifier.
 * @param payload - Permission map; values may be boolean or permission prop.
 * @returns A promise resolving to the updated user permissions.
 */
export const updateUserPermissions = (
  userId: string,
  payload: { [x: string]: boolean | IpermProp }
): Promise<IuserPermission> => {
  const observer$ = AuthClient.ehttp
    .makePut<IuserPermission>(`/auth/update-user-permissions/${userId}`, payload);

  return lastValueFrom(observer$);
};

/**
 * Adds a recovery phone for the current user.
 *
 * @param phone - The phone number to add.
 * @param phoneCode - Country code for the phone number (e.g., +256).
 * @returns A promise resolving to a success and message object.
 */
export const addRecoveryPhone = (phone: string, phoneCode: string): Promise< {
  success: boolean;
  message: string;
}> => {
  const observer$ = AuthClient.ehttp
    .makePost<{ success: boolean; message: string }>('/auth/add-recovery-phone', { phone, phoneCode });

  return lastValueFrom(observer$);
};

/**
 * Verifies a recovery phone using a verification code.
 *
 * @param phone - The recovery phone to verify.
 * @param verificationCode - The received verification code.
 * @returns A promise resolving to a user object.
 */
export const verifyRecoveryPhone = async(phone: string, verificationCode: string): Promise<User> => {
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/verify-recovery-phone', { phone, verificationCode });

  return new User(await lastValueFrom(observer$));
};

/**
 * Removes a recovery phone.
 *
 * @param phone - The phone number to remove.
 * @returns A promise resolving to a user object.
 */
export const removeRecoveryPhone = async(phone: string): Promise<User> => {
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/remove-recovery-phone', { phone });

  return new User(await lastValueFrom(observer$));
};

/**
 * Retrieves the recovery phone numbers for the current user.
 *
 * @returns A promise resolving to an array of recovery phone numbers.
 */
export const getRecoveryPhoneNumbers = (): Promise<string[]> => {
  const observer$ = AuthClient.ehttp
    .makeGet<string[]>('/auth/get-recovery-phone-numbers');

  return lastValueFrom(observer$);
};

/**
 * Retrieves the recovery email addresses for the current user.
 *
 * @returns A promise resolving to an array of recovery email addresses.
 */
export const getRecoveryEmails = (): Promise<string[]> => {
  const observer$ = AuthClient.ehttp
    .makeGet<string[]>('/auth/get-recovery-emails');

  return lastValueFrom(observer$);
};

/**
 * Adds a recovery email for the current user.
 *
 * @param email - The email address to add.
 * @returns A promise resolving to a success and message object.
 */
export const addRecoveryEmail = (email: string): Promise<{success: boolean; message: string}> => {
  const observer$ = AuthClient.ehttp
    .makePost<{success: boolean; message: string}>('/auth/add-recovery-email', { email });

  return lastValueFrom(observer$);
};

/**
 * Verifies a recovery email using a verification code.
 *
 * @param email - The recovery email to verify.
 * @param verificationCode - The received verification code.
 * @returns A promise resolving to a user object.
 */
export const verifyRecoveryEmail = async(email: string, verificationCode: string): Promise<User> => {
  const observer$ = AuthClient.ehttp.makePost<Iuser>('/auth/verify-recovery-email', { email, verificationCode });

  return new User(await lastValueFrom(observer$));
};

/**
 * Removes a recovery email.
 *
 * @param email - The email address to remove.
 * @returns A promise resolving to a user object.
 */
export const removeRecoveryEmail = async(email: string): Promise<User> => {
  const observer$ = AuthClient.ehttp
    .makePost<Iuser>('/auth/remove-recovery-email', { email });

  return new User(await lastValueFrom(observer$));
};

/**
 * Checks if a user exists by email or phone.
 *
 * @param params - Contains optional `email` and `phone` to check.
 * @returns A promise resolving to true if a user exists for either field.
 */
export const existsEmailOrPhone = async({ email, phone }: {email: string; phone: string}): Promise<boolean> => {
  const observer$ = AuthClient.ehttp
    .makePost<{ exists: boolean }>('/auth/exists-email-phone', { email, phone });
  const response = await lastValueFrom(observer$);

  return response.exists;
};


/**
 * Logs the current user out of the application.
 *
 * @returns A promise that resolves when the logout is complete.
 */
export const logout = (): Promise<void> => {
  const observer$ = AuthClient.ehttp
    .makePost('/auth/logout', {});

  return lastValueFrom(observer$);
};

/**
 * Logs the current user into the application using WebSockets.
 *
 * @returns A promise resolving to an auth token response.
 */
export const wsLogin = (): Promise<IwsAuthTokenResponse> => {
  const observer$ = AuthClient.ehttp
    .makePost<IwsAuthTokenResponse>('/auth/ws-login', {});

  return lastValueFrom(observer$);
};

/**
 * Refreshes the current user's authentication token using WebSockets.
 *
 * @returns A promise resolving to a fresh auth token response.
 */
export const wsRefresh = (): Promise<IwsAuthTokenResponse> => {
  const observer$ = AuthClient.ehttp
    .makePost<IwsAuthTokenResponse>('/auth/ws-refresh', {});

  return lastValueFrom(observer$);
};

/**
 * Logs the current user out of the application using WebSockets.
 *
 * @returns A promise that resolves when the logout is complete.
 */
export const wsLogout = (): Promise<void> => {
  const observer$ = AuthClient.ehttp
    .makePost('/auth/ws-logout', {});

  return lastValueFrom(observer$);
};
